import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaTachometerAlt, FaClipboardList, FaUsers, FaSignOutAlt, 
  FaBars, FaTimes, FaUserCircle, FaCog, FaEnvelope, FaShieldAlt, FaSchool, FaPaperPlane, FaHistory, FaImages
} from 'react-icons/fa';
import './AdminLayout.css';

const AdminLayout = ({ children }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      navigate('/admin/login');
    }
  };

  const menuItems = [
    {
      path: '/admin/dashboard',
      icon: <FaTachometerAlt />,
      label: 'Dashboard',
      show: true
    },
    {
      path: '/admin/applications',
      icon: <FaClipboardList />,
      label: 'Applications',
      show: true
    },
    {
      path: '/admin/contacts',
      icon: <FaEnvelope />,
      label: 'Enquiries',
      show: true
    },
    {
      path: '/admin/admission-controls',
      icon: <FaCog />,
      label: 'Admission Controls',
      show: true
    },
    {
      path: '/admin/school-settings',
      icon: <FaSchool />,
      label: 'School Settings',
      show: true
    },
    {
      path: '/admin/gallery',
      icon: <FaImages />,
      label: 'Gallery',
      show: true
    },
    {
      path: '/admin/security',
      icon: <FaShieldAlt />,
      label: 'Security',
      show: true
    },
    {
      path: '/admin/notifications',
      icon: <FaPaperPlane />,
      label: 'Email Notifications',
      show: isSuperAdmin
    },
    {
      path: '/admin/audit',
      icon: <FaHistory />,
      label: 'Audit Log',
      show: isSuperAdmin
    },
    {
      path: '/admin/users',
      icon: <FaUsers />,
      label: 'User Management',
      show: isSuperAdmin
    }
  ];

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <img src="/logo.png" alt="KS Montessori & Secondary School" className="sidebar-logo" />
          {sidebarOpen && <h2>Admin Portal</h2>}
        </div>

        <nav className="sidebar-nav" aria-label="Admin">
          {menuItems.filter(item => item.show).map((item) => {
            const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
                aria-label={sidebarOpen ? undefined : item.label}
                title={sidebarOpen ? undefined : item.label}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button className="btn-logout" onClick={handleLogout} aria-label={sidebarOpen ? undefined : 'Logout'}>
            <FaSignOutAlt aria-hidden="true" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Bar */}
        <header className="admin-topbar">
          <button
            type="button"
            className="btn-toggle-sidebar"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>

          <div className="topbar-user">
            <FaUserCircle />
            <div className="user-info">
              <span className="user-name">{user?.full_name || user?.username}</span>
              <span className="user-role">{user?.role}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-content" id="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
